import { useRouter } from 'expo-router';
import { Compass, MapPin } from 'lucide-react-native';
import { useEffect, useRef, useState } from 'react';
import {
  Keyboard,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';

import { AddressAutocomplete } from '@/components/AddressAutocomplete';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { CLIMATE_ZONE_LABEL, lookupClimate } from '@/data/climate-zip';
import type { ClimateZone } from '@/db/types';
import { fetchPostalCodeFromGps } from '@/lib/geolocation';
import { webNoAutofill, webNoOutline } from '@/lib/web-style';
import { useOnboarding } from '@/store/onboarding';

/**
 * Combined welcome + location step. Captures the user's name and home
 * location together so the rest of onboarding (home type, HVAC, etc.)
 * can lean on a fully-resolved climate zone. Address autocomplete is the
 * primary path — picking a suggestion fills the ZIP and unlocks the My
 * Home map preview. ZIP entry is the fallback.
 */
export default function Welcome() {
  const router = useRouter();
  const {
    setOwnerName,
    setZip,
    setAddress,
    ownerFirstName: storedFirst,
    ownerLastName: storedLast,
    zip: storedZip,
    climateZone: storedZone,
    climateLabel: storedLabel,
    address: storedAddress,
  } = useOnboarding();

  const [first, setFirst] = useState(storedFirst);
  const [last, setLast] = useState(storedLast);
  const [addressInput, setAddressInput] = useState(storedAddress ?? '');
  const [zip, setLocalZip] = useState(storedZip ?? '');
  const [zone, setZone] = useState<ClimateZone | null>(storedZone ?? null);
  const [label, setLabel] = useState<string | null>(storedLabel ?? null);
  const [gpsBusy, setGpsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<ScrollView>(null);
  const addressCardRef = useRef<View>(null);
  const addressFocusedRef = useRef(false);
  /** Live software-keyboard height. Non-zero adds that much bottom
   *  padding to the scroll content — WITHOUT it the page often isn't
   *  tall enough to scroll at all, so no amount of scrollTo could lift
   *  the address card above the keyboard (Wave 60, the real fix for
   *  "suggestions hidden under the keyboard"). */
  const [kbHeight, setKbHeight] = useState(0);

  const scrollAddressIntoView = () => {
    setTimeout(() => {
      if (!addressCardRef.current || !scrollRef.current) return;
      addressCardRef.current.measureLayout(
        // @ts-expect-error — RN's typings here are imprecise but this works.
        scrollRef.current,
        (_x: number, y: number) => {
          scrollRef.current?.scrollTo({ y: Math.max(0, y - 8), animated: true });
        },
        () => {}
      );
    }, 120);
  };

  useEffect(() => {
    const show = Keyboard.addListener('keyboardDidShow', (e) => {
      setKbHeight(e.endCoordinates?.height ?? 320);
      // Re-scroll once the padding exists and the window has settled —
      // keyboardDidShow fires after the resize/pan, which is the only
      // moment the measurement is trustworthy.
      if (addressFocusedRef.current) scrollAddressIntoView();
    });
    const hide = Keyboard.addListener('keyboardDidHide', () => {
      setKbHeight(0);
      addressFocusedRef.current = false;
    });
    return () => {
      show.remove();
      hide.remove();
    };
  }, []);

  const onZipChange = (v: string) => {
    const clean = v.replace(/[^0-9]/g, '').slice(0, 5);
    setLocalZip(clean);
    if (clean.length === 5) {
      const c = lookupClimate(clean);
      if (c) {
        setZone(c.zone);
        setLabel(c.label);
        setZip(clean, c.zone, c.label);
      } else {
        setZone(null);
        setLabel(null);
        setZip(clean, null, null);
      }
    } else {
      setZone(null);
      setLabel(null);
    }
  };

  const useGPS = async () => {
    setError(null);
    setGpsBusy(true);
    try {
      const found = await fetchPostalCodeFromGps();
      if (!found) {
        setError("Couldn't read a ZIP from your location. Type it in instead.");
        return;
      }
      onZipChange(found);
    } catch (err) {
      // Friendly copy only — never raw exception text (Wave 57: a
      // Play-services geocoder failure printed a Java stack fragment
      // into onboarding).
      const msg = err instanceof Error ? err.message : '';
      setError(
        /permission/i.test(msg)
          ? 'Location permission was declined — no problem, just type your ZIP.'
          : "Couldn't look up your location right now (VPNs and spotty connections can block it). Type your ZIP instead."
      );
    } finally {
      setGpsBusy(false);
    }
  };

  // Names are REQUIRED now (Wave 74, user call) — at least a first
  // name, since the whole app greets and exports by it.
  const ready = zone !== null && first.trim().length > 0;

  const next = () => {
    setOwnerName(first.trim(), last.trim());
    router.push('/(onboarding)/path' as never);
  };

  return (
    <ScrollView
      ref={scrollRef}
      contentContainerStyle={[
        styles.container,
        kbHeight > 0 && { paddingBottom: kbHeight + 200 },
      ]}
      showsVerticalScrollIndicator={false}
      keyboardShouldPersistTaps="handled">
      <Text style={styles.eyebrow}>WELCOME</Text>
      <Text style={styles.h1}>
        Every home should{'\n'}come with a <Text style={styles.h1Em}>manual.</Text>
      </Text>
      <Text style={styles.why}>
        Let&apos;s build yours. Tell us a bit about you and where the home is —
        the rest of the year-plan tunes from there.
      </Text>

      <View style={styles.card}>
        <Text style={styles.cardLabel}>YOUR NAME</Text>
        <Text style={styles.cardHint}>
          Your manual is personal — we greet you by first name and put
          your name on exports.
        </Text>
        <View style={styles.row}>
          <TextInput
            value={first}
            onChangeText={setFirst}
            placeholder="First name"
            onBlur={() => setOwnerName(first.trim(), last.trim())}
            placeholderTextColor={Palette.inkFaint}
            autoCapitalize="words"
            autoComplete="given-name"
            style={[styles.input, webNoOutline]}
          />
          <TextInput
            value={last}
            onChangeText={setLast}
            placeholder="Last name"
            onBlur={() => setOwnerName(first.trim(), last.trim())}
            placeholderTextColor={Palette.inkFaint}
            autoCapitalize="words"
            autoComplete="family-name"
            style={[styles.input, webNoOutline]}
          />
        </View>
      </View>

      <View ref={addressCardRef} style={styles.card}>
        <Text style={styles.cardLabel}>HOME ADDRESS</Text>
        <Text style={styles.cardHint}>
          Pick a suggestion to unlock the map preview on My Home. Your
          climate zone fills in automatically.
        </Text>
        <View style={{ marginTop: 10 }}>
          <AddressAutocomplete
            value={addressInput}
            onInputFocus={() => {
              addressFocusedRef.current = true;
              scrollAddressIntoView();
            }}
            onChangeText={(v) => {
              setAddressInput(v);
              if (!v) setAddress(null, null, null);
            }}
            onPick={(detail) => {
              setAddressInput(detail.address);
              setAddress(detail.address, detail.lat, detail.lon);
              if (detail.zip) onZipChange(detail.zip);
            }}
            placeholder="123 Main St, Anytown, MI"
          />
        </View>

        <Text style={[styles.cardLabel, { marginTop: Spacing.three }]}>
          OR JUST ZIP
        </Text>
        <View style={styles.zipRow}>
          <TextInput
            value={zip}
            onChangeText={onZipChange}
            placeholder="ZIP code"
            placeholderTextColor={Palette.inkFaint}
            inputMode="numeric"
            maxLength={5}
            // Browser autofill suppressed on web (Wave 47) — Chrome's
            // address panel was popping over this and the Places field.
            // iOS keeps the keyboard-bar postal hint.
            textContentType="postalCode"
            {...webNoAutofill('zip')}
            style={[styles.zipInput, webNoOutline]}
          />
          <Pressable
            onPress={useGPS}
            disabled={gpsBusy}
            style={({ pressed }) => [styles.gps, pressed && { opacity: 0.9 }]}>
            <MapPin size={14} color={Palette.cream} strokeWidth={2} />
            <Text style={styles.gpsText}>{gpsBusy ? 'Locating…' : 'Use my location'}</Text>
          </Pressable>
        </View>

        {error ? <Text style={styles.error}>{error}</Text> : null}

        {ready ? (
          <View style={styles.climate}>
            <View style={styles.climateIco}>
              <Compass size={18} color={Palette.forest} strokeWidth={1.6} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.climateZone}>
                {CLIMATE_ZONE_LABEL[zone!]} climate
              </Text>
              <Text style={styles.climateLoc}>
                {label} · {zip}
              </Text>
            </View>
          </View>
        ) : null}
      </View>

      <Pressable
        accessibilityRole="button"
        disabled={!ready}
        onPress={next}
        style={({ pressed }) => [
          styles.cta,
          !ready && { opacity: 0.45 },
          pressed && ready && styles.ctaPressed,
        ]}>
        <Text style={styles.ctaText}>Get started</Text>
      </Pressable>
      <Text style={styles.fineprint}>
        {ready
          ? 'Takes about 2 minutes'
          : first.trim().length === 0
          ? 'Add your first name + ZIP to continue'
          : 'Enter your ZIP (or pick an address) to continue'}
      </Text>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    paddingHorizontal: Spacing.five,
    paddingTop: Spacing.three,
    paddingBottom: Spacing.six,
  },
  eyebrow: {
    fontSize: 12,
    fontFamily: Fonts.sansBold,
    letterSpacing: 1.4,
    color: Palette.honeyDeep,
    marginBottom: Spacing.two,
  },
  h1: {
    fontFamily: Fonts.serif,
    fontSize: 26,
    lineHeight: 32,
    color: Palette.ink,
    letterSpacing: -0.4,
  },
  h1Em: {
    fontFamily: Fonts.serifItalic,
  },
  why: {
    marginTop: Spacing.three,
    fontSize: 14,
    lineHeight: 21,
    color: Palette.inkSoft,
    fontFamily: Fonts.sans,
  },
  card: {
    marginTop: Spacing.four,
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: Radius.xl,
    padding: Spacing.four,
  },
  cardLabel: {
    fontFamily: Fonts.sansBold,
    fontSize: 12,
    color: Palette.inkFaint,
    letterSpacing: 0.7,
  },
  cardHint: {
    marginTop: 4,
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.inkSoft,
    lineHeight: 17,
  },
  row: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 12,
  },
  input: {
    flex: 1,
    // React Native for Web renders TextInput as <input>, which has a
    // default intrinsic width (size=20 ≈ 170px) that flex: 1 can't shrink
    // below. Without minWidth: 0, the second column overflows the card on
    // narrow viewports. Native mobile ignores minWidth: 0 — no-op there.
    minWidth: 0,
    borderWidth: 1.5,
    borderColor: Palette.line,
    backgroundColor: Palette.sand,
    borderRadius: 11,
    paddingHorizontal: 12,
    paddingVertical: 11,
    fontFamily: Fonts.sansSemibold,
    fontSize: 14,
    color: Palette.ink,
  },
  zipRow: {
    marginTop: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  zipInput: {
    flex: 1,
    // RNW renders TextInput as <input> with an intrinsic min width that
    // overflows the card unless we explicitly allow shrink. Same fix we
    // applied to the First/Last row above.
    minWidth: 0,
    borderWidth: 1.5,
    borderColor: Palette.line,
    backgroundColor: Palette.sand,
    borderRadius: 11,
    paddingHorizontal: 12,
    paddingVertical: 11,
    fontFamily: Fonts.sansSemibold,
    fontSize: 14,
    color: Palette.ink,
  },
  gps: {
    backgroundColor: Palette.forest,
    borderRadius: 11,
    paddingHorizontal: 13,
    paddingVertical: 11,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    // Hard-cap the button so the pin + label can't grow past their
    // natural width and shove the input out of the card.
    flexShrink: 0,
  },
  gpsText: {
    color: Palette.cream,
    fontFamily: Fonts.sansBold,
    fontSize: 13,
  },
  error: {
    marginTop: Spacing.two,
    color: Palette.clay,
    fontSize: 13,
    fontFamily: Fonts.sansSemibold,
  },
  climate: {
    marginTop: Spacing.three,
    backgroundColor: '#EAF0EA',
    borderRadius: Radius.lg,
    padding: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  climateIco: {
    width: 34,
    height: 34,
    borderRadius: 10,
    backgroundColor: Palette.card,
    alignItems: 'center',
    justifyContent: 'center',
  },
  climateZone: {
    fontSize: 13.5,
    fontFamily: Fonts.sansBold,
    color: Palette.forest,
  },
  climateLoc: {
    fontSize: 12.5,
    color: Palette.inkSoft,
    fontFamily: Fonts.sansSemibold,
    marginTop: 1,
  },
  cta: {
    marginTop: Spacing.four,
    backgroundColor: Palette.forest,
    paddingVertical: Spacing.four,
    borderRadius: Radius.lg,
    alignItems: 'center',
    justifyContent: 'center',
  },
  ctaPressed: {
    opacity: 0.92,
    transform: [{ scale: 0.98 }],
  },
  ctaText: {
    color: Palette.cream,
    fontFamily: Fonts.sansBold,
    fontSize: 16,
    letterSpacing: 0.1,
  },
  fineprint: {
    marginTop: Spacing.three,
    textAlign: 'center',
    fontSize: 13,
    fontFamily: Fonts.sansSemibold,
    color: Palette.inkFaint,
  },
});
